import devConfigJson from "./auth_config.dev.json";
import sandboxConfigJson from "./auth_config.sandbox.json";
import uatConfigJson from "./auth_config.uat.json";

const DEFAULT_ENVIRONMENT = "dev";

// This maps the journey keys to their respective configs for easy lookup
const CONFIG_BY_ENVIRONMENT = {
  dev: devConfigJson,
  sandbox: sandboxConfigJson,
  uat: uatConfigJson,
};

/**
 * Determines the requested environment for the demo app based on the following precedence:
 * 1. Global variable `__DEMO_APP_ENV__` (can be set by hosting environment)
 * 2. Environment variable `VITE_DEMO_APP_ENV` (set at build time)
 * 3. Default environment (`dev`)
 * @returns {string} The requested environment
 */
const getRequestedEnvironment = () =>
  globalThis.__DEMO_APP_ENV__ ||
  process.env.VITE_DEMO_APP_ENV ||
  DEFAULT_ENVIRONMENT;

  /**
   * Determines the selected environment for the demo app based on the following precedence:
   * 1. URL parameter `environment`
   * 2. The requested environment (determined by `getRequestedEnvironment()`)
   * 3. Local storage value `selectedEnvironment`
   * 4. Default environment (`dev`)
   * 
   * @returns 
   */
export function getSelectedDemoAppEnvironment() {
  const requestedEnvironment = getRequestedEnvironment();

  return CONFIG_BY_ENVIRONMENT[requestedEnvironment]
    ? requestedEnvironment
    : DEFAULT_ENVIRONMENT;
}
/**
 * Retrieves the configuration JSON for a given environment.
 * @param {string} environment - The environment for which to retrieve the config.
 * @returns {object} The configuration JSON for the specified environment.
 */
export function getConfigJsonForEnvironment(
  environment = getRequestedEnvironment()
) {
  return CONFIG_BY_ENVIRONMENT[environment];
}
/**
 * Creates a configuration object for the demo app based on the provided config JSON and environment.
 * @param {object} configJson - The configuration JSON for the demo app.
 * @param {string} appEnvironment - The environment for which to create the config.
 * @returns {object} The configuration object for the demo app.
 */
export function createConfig(
  configJson,
  appEnvironment = getSelectedDemoAppEnvironment()
) {
  if (!configJson) {
    throw new Error("No auth configuration is available for the demo app.");
  }

  // Configure the audience here. By default, it will take whatever is in the config
  // (specified by the `audience` key) unless it's the default value of "YOUR_API_IDENTIFIER" (which
  // is what you get sometimes by using the Auth0 sample download tool from the quickstart page, if you
  // don't have an API).
  // If this resolves to `null`, the API page changes to show some helpful info about what to do
  // with the audience.
  const audience =
    configJson.audience && configJson.audience !== "YOUR_API_IDENTIFIER"
      ? configJson.audience
      : null;

  var invitationJourneyKey =
    typeof configJson.invitationJourneyKey === "string" &&
    configJson.invitationJourneyKey.trim()
      ? configJson.invitationJourneyKey.trim()
      : "org_invite";

  return {
    domain: configJson.domain,
    clientId: configJson.clientId,
    journeyConfig: configJson.journeyConfig || {},
    invitationJourneyKey,
    apiUrl: configJson.apiUrl,
    auth0: configJson.auth0,
    appEnvironment,
    ...(audience ? { audience } : null),
  };
}

/**
 * Retrieves the complete configuration object for the demo app.
 * Combines the configuration JSON for the requested environment with the selected environment.
 * @returns {object} The configuration object for the demo app.
 */
export function getConfig() {
  const configJson = getConfigJsonForEnvironment(getRequestedEnvironment());
  const appEnvironment = getSelectedDemoAppEnvironment();

  return createConfig(configJson, appEnvironment);
}

/**
 * Auth0 /authorize uses the `organization` parameter (org id like org_xxx).
 * Journey entries may define `org_id` in JSON; it is sent as `organization` when not already set
 * (e.g. from URL, invitation flow, or authorizationParams.organization).
 *
 * @param {Record<string, unknown>} authorizationParams - Params passed to loginWithRedirect / provider
 * @param {Record<string, unknown>|null|undefined} journeyConfig - Resolved journey block from auth_config.*.json
 * @returns {Record<string, unknown>}
 */
export function applyJourneyOrgToAuthorizationParams(
  authorizationParams,
  journeyConfig
) {
  var base =
    authorizationParams && typeof authorizationParams === "object"
      ? { ...authorizationParams }
      : {};
  if (!journeyConfig || typeof journeyConfig !== "object") {
    return base;
  }
  var orgFromConfig = journeyConfig.org_id;
  if (
    orgFromConfig &&
    base.organization == null &&
    base.org_id == null
  ) {
    base.organization = orgFromConfig;
  }
  return base;
}

/**
 * SPA / native client id from ID token claims (`azp`, or single `aud` entry).
 * @param {Record<string, unknown>|null|undefined} claims
 * @returns {string|undefined}
 */
export function getApplicationClientIdFromIdTokenClaims(claims) {
  if (!claims || typeof claims !== "object") {
    return undefined;
  }
  if (typeof claims.azp === "string" && claims.azp) {
    return claims.azp;
  }
  const aud = claims.aud;
  if (Array.isArray(aud) && aud.length && typeof aud[0] === "string") {
    return aud[0];
  }
  if (typeof aud === "string" && aud) {
    return aud;
  }
  return undefined;
}

/**
 * Find org_id from any journey whose application clientId matches (e.g. ID token `azp`).
 * @param {Record<string, unknown>|null|undefined} journeyConfigMap
 * @param {string|null|undefined} applicationClientId
 * @returns {string|null}
 */
export function findOrgIdByApplicationClientId(journeyConfigMap, applicationClientId) {
  if (
    !journeyConfigMap ||
    typeof journeyConfigMap !== "object" ||
    !applicationClientId ||
    typeof applicationClientId !== "string"
  ) {
    return null;
  }
  for (const key of Object.keys(journeyConfigMap)) {
    const entry = journeyConfigMap[key];
    if (
      entry &&
      typeof entry === "object" &&
      entry.clientId === applicationClientId &&
      entry.org_id
    ) {
      return String(entry.org_id);
    }
  }
  return null;
}

/**
 * applyJourneyOrgToAuthorizationParams, then if organization is still unset, use any journey
 * with the same clientId + org_id (authenticated user: azp may match a different journey key than the UI selection).
 *
 * @param {Record<string, unknown>} authorizationParams
 * @param {Record<string, unknown>|null|undefined} selectedJourneyConfig
 * @param {Record<string, unknown>|null|undefined} allJourneys - getConfig().journeyConfig
 * @param {string|null|undefined} applicationClientId - typically from ID token
 */
export function applyJourneyOrgToAuthorizationParamsWithClientFallback(
  authorizationParams,
  selectedJourneyConfig,
  allJourneys,
  applicationClientId
) {
  const merged = applyJourneyOrgToAuthorizationParams(
    authorizationParams,
    selectedJourneyConfig
  );
  if (
    applicationClientId &&
    merged.organization == null &&
    merged.org_id == null
  ) {
    const org = findOrgIdByApplicationClientId(allJourneys, applicationClientId);
    if (org) {
      merged.organization = org;
    }
  }
  return merged;
}
